import React, { useState, useEffect, useRef  } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { attendanceAPI } from '../services/api';
import { useSelector } from 'react-redux';
import { Modal, Spinner } from 'react-bootstrap';
import { 
  QrCode,
  ClockHistory,
  HourglassSplit,
  Stopwatch,
  Alarm,
  Clock, 
  People, 
  Download,
  ExclamationTriangleFill,
  CheckCircleFill,
  Printer,
  InfoCircle,
  ArrowRepeat,
  XCircle
} from 'react-bootstrap-icons';
import '../CSS/TeacherQRGenerator.css';

function TeacherQRGenerator() {
  const [qrCode, setQrCode] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [duration, setDuration] = useState(5);
  const [selectedClass, setSelectedClass] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeQRs, setActiveQRs] = useState([]);
  const [showQRModal, setShowQRModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const isFirstLoad = useRef(true);
  const user = useSelector(state => state.auth.user);
  const classes = ['6', '7', '8', '9', '10', '11', '12'];

 useEffect(() => {
  fetchActiveQRs();
  isFirstLoad.current = false;
  
  const interval = setInterval(fetchActiveQRs, 10000);
  return () => clearInterval(interval);
}, []);

  useEffect(() => {
    if (!expiresAt) return;

    const timer = setInterval(() => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeLeft('Expired');
        setQrCode(null);
        setExpiresAt(null);
        clearInterval(timer);
      } else {
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

 const fetchActiveQRs = async () => {
  try {
    const response = await attendanceAPI.getActiveQRs();
    const activeQRsData = response.data.data.activeQRs || [];
    setActiveQRs(activeQRsData);
    
    // Only show "no active QR codes" message if it's the first load or user manually refreshes
    if (activeQRsData.length === 0 && !toast.show) {
      // Optional: Show a non-intrusive indicator instead of toast
      console.log('No active QR codes available');
    }
  } catch (error) {
    console.error('Error fetching active QRs:', error);
    // Only show error toast if it's not already showing
    if (!toast.show) {
      setToast({
        show: true,
        message: 'No active QR codes. Please try refreshing or create new one.',
        variant: 'warning'
      });
      setTimeout(() => setToast({ ...toast, show: false }), 3000);
    }
  }
};

  const generateQR = async () => {
    if (!selectedClass) {
      setToast({
        show: true,
        message: 'Please select a class',
        variant: 'warning'
      });
      setTimeout(() => setToast({ ...toast, show: false }), 3000);
      return;
    }

    setLoading(true);
    try {
      const response = await attendanceAPI.generateQR({
        class: selectedClass,
        duration
      });
      
      setQrCode(response.data.data.qrCode);
      setExpiresAt(response.data.data.expiresAt);
      setShowQRModal(true);
      
      setToast({
        show: true,
        message: 'QR Code generated successfully!',
        variant: 'success'
      });
      setTimeout(() => setToast({ ...toast, show: false }), 3000);
      
      fetchActiveQRs();
    } catch (error) {
      console.error('Error generating QR:', error);
      const errorMsg = error.response?.data?.error || 
                       error.response?.data?.message || 
                       'Failed to generate QR code';
      
      setToast({
        show: true,
        message: errorMsg,
        variant: 'danger'
      });
      setTimeout(() => setToast({ ...toast, show: false }), 3000);
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = () => {
    const canvas = document.getElementById('tqg-qr-canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `qr-code-class-${selectedClass}.png`;
      link.href = url;
      link.click();
    }
  };

  const closeToast = () => {
    setToast({ ...toast, show: false });
  };

  return (
    <div className="tqg-wrapper">
      <div className="tqg-container">
        <div className="tqg-card">
          <div className="tqg-card-header">
            <QrCode className="tqg-header-icon" />
            <h4 className="tqg-header-title">Generate Attendance QR Code</h4>
          </div>
          
          <div className="tqg-card-body">
            <div className="tqg-grid">
              <div className="tqg-left-panel">
                <div className="tqg-form-group">
                  <label className="tqg-label">
                    <People className="tqg-label-icon" />
                    Select Class
                  </label>
                  <select 
                    className="tqg-select"
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                  >
                    <option value="">Choose class...</option>
                    {classes.map(cls => (
                      <option key={cls} value={cls}>Class {cls}</option>
                    ))}
                  </select>
                </div>

                <div className="tqg-form-group">
                  <label className="tqg-label">
                    <Stopwatch className="tqg-label-icon" />
                    QR Code Duration
                  </label>
                  <select 
                    className="tqg-select"
                    value={duration}
                    onChange={(e) => setDuration(parseInt(e.target.value))}
                  >
                    <option value={1}>1 minute</option>
                    <option value={2}>2 minutes</option>
                    <option value={5}>5 minutes</option>
                    <option value={10}>10 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={60}>60 minutes</option>
                  </select>
                  <small className="tqg-hint">
                    QR code will automatically expire after {duration} minute{duration > 1 ? 's' : ''}
                  </small>
                </div>

                <button 
                  className="tqg-btn tqg-btn-primary tqg-btn-block"
                  onClick={generateQR}
                  disabled={loading || !selectedClass}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="tqg-spinner" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <QrCode className="tqg-btn-icon" />
                      Generate New QR Code
                    </>
                  )}
                </button>
              </div>

              <div className="tqg-right-panel">
                <div className="tqg-active-qrs">
                  <h5 className="tqg-active-title">
                    <ArrowRepeat className="tqg-active-icon" />
                    Currently Active QR Codes
                  </h5>
                  
                  {activeQRs.length === 0 ? (
                    <>
                    <div className="tqg-empty-state">
                      <XCircle className="tqg-empty-icon" />
                      <p className="tqg-empty-text">No active QR codes</p>
                    </div>
                    {!loading && (
                    <div className="tqg-info-message">
                      <InfoCircle className="tqg-info-icon-small" />
                      <span>No active QR codes. Generate one above to get started.</span>
                    </div>
                  )}
                  </>
                  ) : (
                    <div className="tqg-qr-list">
                      {activeQRs.map((qr, index) => (
                        <div key={index} className="tqg-qr-item">
                          <div className="tqg-qr-item-header">
                            <span className="tqg-class-badge">Class {qr.class}</span>
                            <small className="tqg-expiry-time">
                               <ClockHistory className="me-1" size={14} />
                              Expires: {new Date(qr.expiresAt).toLocaleTimeString()}
                            </small>
                          </div>
                          <div className="tqg-qr-stats">
                            <strong>Scanned:</strong> {qr.usedCount} student{qr.usedCount !== 1 ? 's' : ''}
                          </div>
                          {qr.usedBy && qr.usedBy.length > 0 && (
                            <div className="tqg-recent-scans">
                              <strong>Recent scans:</strong>
                              <ul className="tqg-scan-list">
                                {qr.usedBy.slice(0, 3).map((scan, i) => (
                                  <li key={i} className="tqg-scan-item">
                                    {scan.studentName} - {new Date(scan.timestamp).toLocaleTimeString()}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Display Modal */}
      <Modal 
        show={showQRModal} 
        onHide={() => setShowQRModal(false)} 
        centered 
        size="lg"
        className="tqg-modal"
        dialogClassName="tqg-modal-dialog"
        contentClassName="tqg-modal-content"
      >
        <Modal.Header closeButton className="tqg-modal-header">
          <Modal.Title className="tqg-modal-title">
            QR Code for Class {selectedClass}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="tqg-modal-body">
          {qrCode && (
            <>
              <div className="tqg-qr-display">
                <div className="tqg-qr-container">
                  <QRCodeCanvas 
                    id="tqg-qr-canvas"
                    value={qrCode} 
                    size={300}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                
                <div className="tqg-timer-display">
                  <h5 className={timeLeft === 'Expired' ? 'tqg-timer-expired' : 'tqg-timer-active'}>
                    {timeLeft === 'Expired' ? (
                      <> <Alarm className="me-2" size={24} style={{ color: 'var(--tqg-danger)' }} />
                       Expired
                       </>
                    ) : (
                      <> <HourglassSplit className="me-2" size={24} style={{ color: 'var(--tqg-success)' }} />
                      Valid for: {timeLeft}
                      </>
                    )}
                  </h5>
                </div>

                <div className="tqg-action-buttons">
                  <button 
                    className="tqg-btn tqg-btn-outline"
                    onClick={downloadQR}
                  >
                    <Download className="tqg-btn-icon" />
                    Download QR Code
                  </button>
                  <button 
                    className="tqg-btn tqg-btn-primary"
                    onClick={() => window.print()}
                  >
                    <Printer className="tqg-btn-icon" />
                    Print QR Code
                  </button>
                </div>

                <div className="tqg-info-box">
                  <InfoCircle className="tqg-info-icon" />
                  <div className="tqg-info-content">
                    <strong>Instructions:</strong> Display this QR code in your classroom.
                    Students can scan it to mark their attendance.
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Toast Notification */}
      {toast.show && (
        <div className={`tqg-toast tqg-toast-${toast.variant}`}>
          <div className="tqg-toast-header">
            <strong className="tqg-toast-title">
              {toast.variant === 'success' ? 'Success' : 'Error'}
            </strong>
            <button className="tqg-toast-close" onClick={closeToast}>×</button>
          </div>
          <div className="tqg-toast-body">
            {toast.variant === 'success' ? (
              <CheckCircleFill className="tqg-toast-icon" />
            ) : (
              <ExclamationTriangleFill className="tqg-toast-icon" />
            )}
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherQRGenerator;